/** @jsxImportSource theme-ui */
import React, { useCallback, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Flex,
  Text,
  Button,
  Card,
  useToastContext,
  Spinner,
} from '@infotrack/zenith-ui';

import { useAuthUrls } from '@/auth/hooks/useAuthUrls';
import { useITopFiveApiV2EndpointsAuthMeEndpoint } from '@/api/api.generated';
import {
  containerStyles,
  contentCardStyles,
  contentStyles,
  textSectionStyles,
} from './login.styles';

/**
 * Login Component
 *
 * Login page for Microsoft Entra ID SSO with BFF pattern.
 * Matches Figma design specifications exactly.
 */
export const Login = () => {
  const { redirectToLogin } = useAuthUrls();
  const navigate = useNavigate();
  const location = useLocation() as {
    state?: { from?: { pathname?: string } };
  };
  const [isLoading, setIsLoading] = useState(false);
  const toastContext = useToastContext();

  // Check if user is already authenticated
  const {
    data: authResponse,
    isLoading: isAuthLoading,
  } = useITopFiveApiV2EndpointsAuthMeEndpoint({
    query: {
      retry: false, // Don't retry for login page
    },
  });

  // Redirect to topfive if already authenticated
  useEffect(() => {
    if (!isAuthLoading && authResponse?.data) {
      const returnPath = location.state?.from?.pathname || '/topfive';
      navigate(returnPath, { replace: true });
    }
  }, [isAuthLoading, authResponse, navigate, location.state]);

  const showError = useCallback(
    (message: string) => {
      toastContext.error({
        title: 'Sign In Failed',
        message,
        duration: 5,
      });
    },
    [toastContext]
  );

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const returnPath = location.state?.from?.pathname || '/topfive';
      setIsLoading(true);

      try {
        redirectToLogin(returnPath);
      } catch {
        setIsLoading(false);
        showError('Unable to sign in. Please try again.');
      }
    },
    [redirectToLogin, location.state, showError]
  );

  // Show loading state while checking authentication
  if (isAuthLoading) {
    return (
      <Flex sx={containerStyles} role="main">
        <Spinner fullSize />
      </Flex>
    );
  }

  return (
    <Flex sx={containerStyles} role="main">
      <Card
        padding={['3rem']}
        borderRadius="medium"
        borderColor="neutral/200"
        sx={contentCardStyles}
        role="region"
        aria-labelledby="login-title"
      >
        <Flex sx={contentStyles}>
          <Flex sx={textSectionStyles}>
            <Text variant="hero">iTop5</Text>
            <Text variant="medium">
              Sign in with your Microsoft account to continue.
            </Text>
          </Flex>

          <form
            onSubmit={handleSubmit}
            aria-labelledby="login-title"
            aria-describedby="login-description"
          >
            <Button
              type="submit"
              size="large"
              variant="primary"
              fullWidth
              loading={isLoading}
            >
              Sign in with Microsoft
            </Button>
          </form>
        </Flex>
      </Card>
    </Flex>
  );
};
